// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import Close from "@synd/XDL/Icons/Close";
import AlertError from "@synd/XDL/Icons/NotificationErrorCircle";
import AlertInfo from "@synd/XDL/Icons/NotificationInfoCircle";
import AlertSuccess from "@synd/XDL/Icons/NotificationSuccessCheckCircle";
import AlertWarning from "@synd/XDL/Icons/NotificationWarningTriangle";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, getSystemColor, mergeWithCss } from "@synd/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends TypographyProps {
    type?: "success" | "danger" | "warning" | "info";
    showClose?: boolean;
    onClose?: () => void;
    fullWidth?: boolean;
}

type Props = OwnProps;

const base64Icons = {
    success: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 1.375a7.875 7.875 0 1 1 0 15.75 7.875 7.875 0 0 1 0-15.75M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18m3.772-10.853a.564.564 0 0 0 0-.794.564.564 0 0 0-.794 0l-4.103 4.102-1.853-1.852a.564.564 0 0 0-.794 0 .564.564 0 0 0 0 .794l2.25 2.25a.564.564 0 0 0 .794 0z'/%3E%3C/svg%3E`,
    danger: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 1.375a7.875 7.875 0 1 1 0 15.75 7.875 7.875 0 0 1 0-15.75M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18M6.353 6.603a.564.564 0 0 0 0 .794L8.205 9.25l-1.852 1.853a.564.564 0 0 0 0 .794.564.564 0 0 0 .794 0L9 10.045l1.853 1.852a.564.564 0 0 0 .794 0 .564.564 0 0 0 0-.794L9.795 9.25l1.852-1.853a.564.564 0 0 0 0-.794.564.564 0 0 0-.794 0L9 8.455 7.147 6.603a.564.564 0 0 0-.794 0'/%3E%3C/svg%3E`,
    warning: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 17'%3E%3Cpath fill='${iconColor}' d='M1.213 14.246a.7.7 0 0 0-.088.333.65.65 0 0 0 .647.657h14.456a.65.65 0 0 0 .647-.657.7.7 0 0 0-.088-.333L9.721 1.94A.84.84 0 0 0 9 1.518c-.295 0-.57.16-.72.421zm-.97-.578L7.309 1.36C7.661.75 8.304.375 9 .375s1.34.375 1.691.986l7.066 12.307c.159.275.243.59.243.907 0 .993-.795 1.8-1.772 1.8H1.772c-.977 0-1.772-.807-1.772-1.8 0-.318.084-.632.243-.907M9 4.946a.57.57 0 0 1 .563.572v4.571a.57.57 0 0 1-.563.572.57.57 0 0 1-.562-.572V5.518A.57.57 0 0 1 9 4.946m-.844 8A.85.85 0 0 1 9 12.09a.85.85 0 0 1 .844.857.85.85 0 0 1-.844.858.85.85 0 0 1-.844-.858'/%3E%3C/svg%3E`,
    info: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19'%3E%3Cpath fill='${iconColor}' d='M9 1.375a7.875 7.875 0 1 1 0 15.75 7.875 7.875 0 0 1 0-15.75M9 18.25a9 9 0 1 0 0-18 9 9 0 0 0 0 18m-1.687-5.625a.564.564 0 0 0-.563.563c0 .309.253.562.563.562h3.375a.564.564 0 0 0 .562-.562.564.564 0 0 0-.562-.563H9.563V8.688A.564.564 0 0 0 9 8.124H7.594a.564.564 0 0 0-.563.563c0 .309.253.562.563.562h.844v3.375zM9 6.719A.844.844 0 1 0 9 5.03a.844.844 0 0 0 0 1.69'/%3E%3C/svg%3E`,
};

const getNotifyIcons = (type: OwnProps["type"], useBase64 = true) => {
    const baseColor = getSystemColor(`${type}.main`, 1, true);
    const iconColor = useBase64 ? getSystemColor(`${type}.main`, 1, true).replace("#", "%23") : baseColor;
    switch (type) {
        case "success":
            return useBase64 ? (
                base64Icons["success"](iconColor)
            ) : (
                <Icon src={AlertSuccess} color={iconColor} size={18} />
            );
        case "danger":
            return useBase64 ? base64Icons["danger"](iconColor) : <Icon src={AlertError} color={iconColor} size={18} />;
        case "warning":
            return useBase64 ? (
                base64Icons["warning"](iconColor)
            ) : (
                <Icon src={AlertWarning} color={iconColor} size={18} />
            );
        case "info":
            return useBase64 ? base64Icons["info"](iconColor) : <Icon src={AlertInfo} color={iconColor} size={18} />;
    }
};

export const getNotifyStyle = (type: OwnProps["type"], useBase64 = false, fullWidth = false): TypographyProps => {
    return {
        role: "status",
        "aria-live": "polite",
        "data-test-selector": `notify-${type}`,
        css: css`
            position: relative;
            display: flex;
            padding: 8px 12px;
            text-align: left;
            border-radius: 4px;
            border: 1px solid ${color(`${type}.main`)};
            background-color: ${color(`${type}.light`)};
            width: 100%;
            min-width: 200px;
            margin: 0 0 16px;
            color: ${color("text.main")};
            letter-spacing: 0;
            font-size: 14px;
            line-height: 22px;

            ${fullWidth
                ? css`
                      max-width: 100%;
                  `
                : css`
                      max-width: 543px;
                  `}

            &.light-mode {
                border: none;
                border-radius: 8px;
                padding: 6px;
                font-size: ${fontConfig.xxSmallSingleLine.size}px;
                line-height: ${fontConfig.xxSmallSingleLine.lineHeight};
                font-weight: ${fontConfig.weight.regular};
                .notify-icon-wrapper {
                    margin-right: 6px;
                    ${IconWrapper} {
                        &,
                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }
            }
            .notify-left-side {
                display: flex;
                align-items: flex-start;
            }
            .notify-icon-wrapper {
                &:empty {
                    display: none;
                }
                display: flex;
                margin-right: 8px;
                margin-top: 2px;
            }
            .notify-close-wrapper {
                cursor: pointer;
                margin-left: 18px;
                ${IconWrapper} {
                    color: ${color("text.main")};
                    &:hover {
                        color: ${color("common.ui10")};
                    }
                }
            }
            .notify-content-wrapper {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
            }
            @media print {
                -webkit-print-color-adjust: exact !important; /* Chrome, Safari 6 – 15.3, Edge */
                color-adjust: exact !important; /* Firefox 48 – 96 */
                print-color-adjust: exact !important; /* Firefox 97+, Safari 15.4+ */
            }

            ${useBase64
                ? `
                    .notify-close-wrapper {
                        margin-left: auto;
                    }
                    &:before {
                    height: 18px;
                    width: 18px;
                    min-width: 18px;
                    margin-right: 8px;
                    content: " ";
                    margin-top: 2px;
                    background-repeat: no-repeat;
                    background-image: url("${getNotifyIcons(type, useBase64)}");
                    }
                `
                : `
                    justify-content: space-between;
                   `}
        `,
    };
};

const Notify: FC<Props> = ({
    type,
    className,
    children,
    css: extendedCssStyle,
    showClose = false,
    onClose,
    fullWidth = false,
}) => {
    const [styles] = React.useState(() =>
        mergeWithCss(getNotifyStyle(type, false, fullWidth), { css: extendedCssStyle }),
    );
    const [visible, setVisibility] = React.useState(true);

    if (!React.Children.count(children) || !visible) {
        return null;
    }

    const handleCloseNotify = () => {
        onClose?.();
        setVisibility(false);
    };

    const classNames = ["notify", type, className || ""].join(" ").trim();
    return (
        <Typography {...styles} className={classNames}>
            <Typography className="notify-left-side">
                <Typography className="notify-icon-wrapper">{getNotifyIcons(type, false)}</Typography>
                <Typography className="notify-content-wrapper">{children}</Typography>
            </Typography>
            {showClose && (
                <Typography
                    className="notify-close-wrapper"
                    onClick={handleCloseNotify}
                    role="button"
                    aria-label="Close"
                >
                    <Icon src={Close} size={10} />
                </Typography>
            )}
        </Typography>
    );
};

export default Notify;
